'use client';

import { type ReactNode } from 'react';

import { Box, HStack, Separator, Stack, Text, VStack } from '@chakra-ui/react';
import { Button } from '@m/components-ui/button';
import { CostRow, SectionHeader } from '@m/components-ui/mobile-rows';
import { LocationDetailsPanel } from '@m/components-ui/reservation/location-details-panel';
import {
  Accessible,
  Autorenew,
  BatteryChargingFull,
  EvStation,
  Roofing,
  SupportAgent,
  TireRepair,
  ValetCheckout,
  ValetReady,
  Wc,
} from '@m/icons';
import PaymentAmex from '@m/icons/metropolis/company-icons/PaymentAmex';
import PaymentDiscover from '@m/icons/metropolis/company-icons/PaymentDiscover';
import PaymentMastercard from '@m/icons/metropolis/company-icons/PaymentMastercard';
import PaymentVisa from '@m/icons/metropolis/company-icons/PaymentVisa';
import CorrectVehicle from '@m/icons/metropolis/vehicle/CorrectVehicle';
import DriveOut from '@m/icons/metropolis/vehicle/DriveOut';
import {
  Zone_AcceptedPayment,
  Zone_Amenity,
  Zone_SiteMode,
} from '@m-service/engagement-api/metropolis/engagement/marketing_page/v1/marketing_page_pb';

import { FIND_PARKING_URL } from '@/constants/links';

import { ZoneBookingCard } from './ZoneBookingCard';

import type { RateRow } from '@/services/formatRateSpans';
import type { HoursRow, HowItWorksStep } from '@m/components-ui/reservation/location-details-panel';

const AMENITY_ICONS: Record<Zone_Amenity, ReactNode> = {
  [Zone_Amenity.UNSPECIFIED]: null,
  [Zone_Amenity.VALET]: <ValetCheckout />,
  [Zone_Amenity.COVERED]: <Roofing />,
  [Zone_Amenity.EV_CHARGING]: <EvStation />,
  [Zone_Amenity.WHEELCHAIR_ACCESSIBLE]: <Accessible />,
  [Zone_Amenity.PUBLIC_RESTROOM]: <Wc />,
  [Zone_Amenity.ON_SITE_STAFF]: <SupportAgent />,
  [Zone_Amenity.IN_OUT_ACCESS]: <Autorenew />,
  [Zone_Amenity.BATTERY_JUMP]: <BatteryChargingFull />,
  [Zone_Amenity.TIRE_INFLATION]: <TireRepair />,
};

const AMENITY_LABELS: Record<Zone_Amenity, string> = {
  [Zone_Amenity.UNSPECIFIED]: '',
  [Zone_Amenity.VALET]: 'Valet',
  [Zone_Amenity.COVERED]: 'Covered',
  [Zone_Amenity.EV_CHARGING]: 'EV charging',
  [Zone_Amenity.WHEELCHAIR_ACCESSIBLE]: 'Wheelchair accessible',
  [Zone_Amenity.PUBLIC_RESTROOM]: 'Public restroom',
  [Zone_Amenity.ON_SITE_STAFF]: 'On-site staff',
  [Zone_Amenity.IN_OUT_ACCESS]: 'In & out access',
  [Zone_Amenity.BATTERY_JUMP]: 'Battery jump',
  [Zone_Amenity.TIRE_INFLATION]: 'Tire inflation',
};

const PAYMENT_ICONS: Partial<Record<Zone_AcceptedPayment, ReactNode>> = {
  [Zone_AcceptedPayment.AMEX]: <PaymentAmex />,
  [Zone_AcceptedPayment.DISCOVER]: <PaymentDiscover />,
  [Zone_AcceptedPayment.MASTERCARD]: <PaymentMastercard />,
  [Zone_AcceptedPayment.VISA]: <PaymentVisa />,
};

const howItWorksImage = (icon: ReactNode) => (
  <Box as="span" color="brand.700" display="flex">
    {icon}
  </Box>
);

// Self-park (Vision) instructions: drivers park their own vehicle and our
// cameras handle entry/exit.
const SELF_PARK_HOW_IT_WORKS: HowItWorksStep[] = [
  {
    image: howItWorksImage(<CorrectVehicle width={56} height={56} />),
    description:
      'Confirm your vehicle’s license plate matches your reservation information. Then, just drive in! Our cameras will recognize your vehicle and open the gate.',
  },
  {
    image: howItWorksImage(<DriveOut width={56} height={56} />),
    description:
      'When you’re ready to go, just drive out! The gate will open and we’ll text your receipt.',
  },
];

// Valet instructions: an attendant parks and retrieves the vehicle.
const VALET_HOW_IT_WORKS: HowItWorksStep[] = [
  {
    image: howItWorksImage(<ValetReady width={56} height={56} />),
    description:
      'Confirm your vehicle’s license plate matches your reservation information. Then, just drive in and an attendant will scan your plate and park your car for you.',
  },
  {
    image: howItWorksImage(<ValetCheckout width={56} height={56} />),
    description:
      'When you’re ready to go, request your vehicle remotely from the link we texted upon arrival or check in with the valet attendant.',
  },
];

interface ZoneDetailsPanelProps {
  acceptedPayments: Zone_AcceptedPayment[];
  amenities: Zone_Amenity[];
  bookNowUrl: string;
  coordinates?: { latitude: number; longitude: number };
  directionsUrl?: string;
  entranceAddress?: string;
  firstPartyReservationsEnabled: boolean;
  heightClearance?: string;
  hours: HoursRow[];
  imageUrl?: string;
  locationName: string;
  rateRows: RateRow[];
  siteId: string;
  /** Parking mode of the lot — drives valet vs self-park instructions. */
  siteMode: Zone_SiteMode;
  /** IANA timezone of the lot, used to build a lot-local Book now link. */
  timezone?: string;
}

export function ZoneDetailsPanel({
  acceptedPayments,
  amenities,
  bookNowUrl,
  coordinates,
  directionsUrl,
  entranceAddress,
  firstPartyReservationsEnabled,
  heightClearance,
  hours,
  imageUrl,
  locationName,
  rateRows,
  siteId,
  siteMode,
  timezone,
}: ZoneDetailsPanelProps) {
  // Combo (valet + vision) sites default to self-park instructions for now —
  // pending product guidance on what combo sites should display.
  const howItWorks = siteMode === Zone_SiteMode.VALET ? VALET_HOW_IT_WORKS : SELF_PARK_HOW_IT_WORKS;

  const visibleAmenities = amenities
    .filter((a) => a !== Zone_Amenity.UNSPECIFIED)
    .map((a) => ({ icon: AMENITY_ICONS[a], label: AMENITY_LABELS[a] }));

  const visiblePayments = acceptedPayments
    .filter((p) => p !== Zone_AcceptedPayment.UNSPECIFIED)
    .filter((p) => PAYMENT_ICONS[p] !== undefined);
  const acceptedPaymentsNode = (
    <HStack gap="4" flexWrap="wrap">
      {visiblePayments.map((p) => (
        <span key={p}>{PAYMENT_ICONS[p]}</span>
      ))}
    </HStack>
  );

  const needToKnowSection = undefined;

  const driveUpRatesSection =
    rateRows.length > 0 ? (
      <VStack align="stretch" gap="0" px={{ base: '4', md: '0' }}>
        <Separator />
        <SectionHeader title="Drive-up rates">
          Drive-up rates may differ from the reservation rates
        </SectionHeader>
        <VStack align="stretch" gap="0" pt="2" pb="6">
          {rateRows.map(({ label, amount }, index) => (
            <CostRow key={`${label}-${amount}-${index}`} title={label} amount={amount} />
          ))}
        </VStack>
      </VStack>
    ) : undefined;

  const findParkingUrl = coordinates
    ? `${FIND_PARKING_URL}?lat=${coordinates.latitude}&lng=${coordinates.longitude}`
    : FIND_PARKING_URL;

  const findParkingButton = (
    <Button asChild size="md" variant="primary" w="full">
      <a href={findParkingUrl} rel="noopener noreferrer" target="_blank">
        Find parking nearby
      </a>
    </Button>
  );

  return (
    <>
      <LocationDetailsPanel
        backNavHref={findParkingUrl}
        locationName={locationName}
        entranceAddress={entranceAddress}
        imageUrl={imageUrl}
        additionalSections={
          <>
            {driveUpRatesSection}
            {needToKnowSection}
          </>
        }
        amenities={visibleAmenities}
        acceptedPayments={acceptedPaymentsNode}
        hours={hours}
        howItWorks={howItWorks}
        freeCancellation
        bookingWidget={
          firstPartyReservationsEnabled ? (
            <ZoneBookingCard
              bookNowUrl={bookNowUrl}
              coordinates={coordinates}
              siteId={siteId}
              timezone={timezone}
            />
          ) : (
            <Stack
              hideBelow="md"
              bg="fill.background.default"
              border="1px solid"
              borderColor="stroke.divider.emphasized"
              borderRadius="2xl"
              gap="3"
              pb="6"
              pt="2"
              px="6"
            >
              <SectionHeader title="Reserve parking">
                Reservations aren&apos;t available here. Find nearby parking available for
                reservation.
              </SectionHeader>
              <Separator />
              {findParkingButton}
            </Stack>
          )
        }
        directionsHref={directionsUrl}
      />

      {!firstPartyReservationsEnabled && (
        <>
          {/* Spacer prevents the fixed bar from overlapping page content */}
          <Box hideFrom="md" h="28" />
          <Stack
            hideFrom="md"
            position="fixed"
            bottom={0}
            left={0}
            right={0}
            bg="fill.background.default"
            borderTop="1px solid"
            borderColor="stroke.divider.default"
            gap="4"
            px="4"
            pt="2"
            pb="4"
            zIndex={100}
          >
            <Text variant="SmallBodyRegular" color="text.secondary" textAlign="center">
              Reservations aren&apos;t available here. Find nearby parking available for
              reservation.
            </Text>
            {findParkingButton}
          </Stack>
        </>
      )}
    </>
  );
}
